import styled, { keyframes } from 'styled-components';
import breakpoints from 'theme/atoms/breakpoints';
import colours from 'theme/atoms/colours';
import typography from 'theme/atoms/typography';
import * as Buttons from 'theme/components/Buttons.style';
import * as Text from 'theme/components/Text.style';
import { BaseCard } from 'theme/components/Cards.style';

export const RESOLVED_COLOUR = '#00D712';
export const OPEN_COLOUR = '#FF4C16';
export const EMPTY_COLOUR = colours.neutral[300];

const cardGrowIn = keyframes`
  from {
    opacity: 0.6;
    transform: scale(0.97);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

export const Card = styled(BaseCard)`
  align-items: center;
  animation: ${cardGrowIn} 0.4s ease-out;
  border-radius: 30px;
  gap: 15px;
  padding: 15px;
  transform-origin: top;

  &[role='button'] {
    cursor: pointer;
  }

  &[role='button']:focus-visible {
    outline: 2px solid ${colours.neutral[400]};
    outline-offset: 2px;
  }
`;

export const Header = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: center;
  width: 100%;
`;

export const HeaderTitleColumn = styled.div`
  align-items: flex-start;
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  gap: 2px;
  justify-content: center;
  min-width: 0;
`;

export const HeaderLegendColumn = styled.div`
  align-items: flex-end;
  align-self: stretch;
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
`;

export const Title = styled(Text.P3Bold)`
  color: ${colours.neutral[900]};
  width: 100%;
`;

export const DepartmentMenuWrapper = styled.div`
  & > div {
    min-width: 0;
    min-height: 0;
  }
`;

export const DepartmentTrigger = styled.button`
  ${typography.label}
  align-items: center;
  appearance: none;
  background: none;
  border: none;
  color: ${colours.neutral[600]};
  cursor: pointer;
  display: flex;
  gap: 2px;
  margin: 0;
  padding: 0;
  text-align: left;
  width: 100%;

  &:focus-visible {
    outline: 2px solid ${colours.neutral[400]};
    outline-offset: 2px;
    border-radius: 4px;
  }

  svg {
    height: 16px;
    width: 16px;
  }

  svg path {
    stroke: ${colours.neutral[600]};
  }
`;

export const LegendInline = styled.div`
  ${typography.label}
  align-items: center;
  background: none;
  border: none;
  border-radius: 13px;
  color: ${colours.neutral[900]};
  display: flex;
  gap: 5px;
  margin: 0;
  padding: 1px 0;
`;

export const LegendIcon = styled.span<{ $colour: string }>`
  align-items: center;
  color: ${({ $colour }) => $colour};
  display: inline-flex;
  flex-shrink: 0;
  height: 16px;
  justify-content: center;
  width: 16px;

  svg {
    height: 16px;
    width: 16px;
  }

  svg path {
    stroke: ${({ $colour }) => $colour};
  }
`;

export const ChartBlock = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: center;
  width: 100%;
`;

export const Sparkline = styled.div`
  display: flex;
  gap: 3px;
  height: 20px;
  width: 100%;
`;

export const SparklineBar = styled.div<{
  $colour: string;
  $isFirst?: boolean;
  $isLast?: boolean;
}>`
  background-color: ${({ $colour }) => $colour};
  border-radius: ${({ $isFirst, $isLast }) => {
    const left = $isFirst ? '10px' : '2px';
    const right = $isLast ? '10px' : '2px';
    return `${left} ${right} ${right} ${left}`;
  }};
  flex: 1;
  height: 100%;
  min-width: 0;
`;

export const ArchWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  position: relative;
  width: 100%;
`;

export const ArchSvg = styled.svg`
  display: block;
  height: auto;
  max-width: 220px;
  width: 100%;
`;

export const ArchCentre = styled.div`
  align-items: center;
  bottom: 4px;
  display: flex;
  flex-direction: column;
  left: 50%;
  pointer-events: none;
  position: absolute;
  text-align: center;
  transform: translateX(-50%);
  width: 120px;
`;

export const ArchNumber = styled(Text.H3)`
  color: ${colours.neutral[900]};
`;

export const ArchLabel = styled(Text.Label)`
  color: ${colours.neutral[900]};
`;

export const EmptyMessage = styled.div`
  ${typography.label}
  color: ${colours.neutral[500]};
  text-align: center;
  white-space: pre-line;
`;

export const SummaryText = styled.div`
  ${typography.p2}
  color: ${colours.neutral[900]};
  white-space: pre-wrap;
  width: 100%;

  p {
    margin: 0 0 20px 0;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

export const BottomActions = styled.div`
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  width: 100%;

  & > div {
    min-width: 0;
    min-height: 0;
  }
`;

export const MenuButton = styled(Buttons.SecondaryLightIcon).attrs({
  $size: 'tiny',
})`
  flex-shrink: 0;
`;

export const CollapseButton = styled(Buttons.SecondaryLight).attrs({
  $size: 'tiny',
})`
  flex-grow: 0;
  flex-shrink: 0;
  width: fit-content;

  @media (max-width: ${breakpoints.laptop}) {
    flex-grow: 0;
    width: fit-content;
  }
`;

export const PreferencesHeader = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 5px;
  justify-content: center;
  padding-top: 15px;
  text-align: center;
  width: 100%;
  word-break: break-word;
`;

export const PreferencesTitle = styled(Text.P3Bold)`
  line-height: 22px;
  text-align: center;
  width: 100%;
`;

export const PreferencesSubtitle = styled(Text.P2)`
  color: ${colours.neutral[600]};
  text-align: center;
  width: 100%;
`;

export const PreferencesChipGrid = styled.div`
  align-content: flex-start;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  justify-content: center;
  padding: 15px 0;
  width: 100%;
`;

export const PreferencesChip = styled.button<{ $selected: boolean }>`
  align-items: center;
  appearance: none;
  background-color: ${({ $selected }) =>
    $selected ? colours.neutral[900] : 'transparent'};
  backdrop-filter: blur(8px);
  border: 2px solid
    ${({ $selected }) =>
      $selected ? colours.neutral[900] : colours.neutral[200]};
  border-radius: 30px;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  gap: 10px;
  justify-content: center;
  padding: 8px 8px 8px 13px;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:focus-visible {
    outline: 2px solid ${colours.neutral[400]};
    outline-offset: 2px;
  }
`;

export const PreferencesChipLabel = styled(Text.LabelSemibold)<{
  $selected: boolean;
}>`
  color: ${({ $selected }) =>
    $selected ? colours.neutral[100] : colours.neutral[900]};
  white-space: nowrap;
`;

export const PreferencesChipCheckbox = styled.span<{ $selected: boolean }>`
  align-items: center;
  background-color: ${({ $selected }) =>
    $selected ? colours.neutral[100] : 'transparent'};
  border: 2px solid
    ${({ $selected }) =>
      $selected ? colours.neutral[100] : colours.neutral[300]};
  border-radius: 11px;
  box-sizing: border-box;
  display: inline-flex;
  flex-shrink: 0;
  height: 18px;
  justify-content: center;
  line-height: 0;
  width: 18px;

  svg {
    display: block;
    height: 12px;
    width: 12px;
  }

  svg path {
    stroke: ${({ $selected }) =>
      $selected ? colours.neutral[900] : 'transparent'};
    stroke-width: 2;
  }
`;

export const PreferencesSaveButton = styled(Buttons.Primary).attrs({
  $size: 'medium',
  stretch: true,
})`
  width: 100%;
`;
